import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ALL_VARIANTS, allPages, IPage, Variant } from "../models/Page";

export const VARIANT_PARAM = "variant";
const STORAGE_KEY = "docs:variant";

function isVariant(value: string | null | undefined): value is Variant {
  return !!value && (ALL_VARIANTS as string[]).includes(value);
}

function readStoredVariant(): Variant | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredVariant(variant: Variant | null) {
  try {
    if (variant) sessionStorage.setItem(STORAGE_KEY, variant);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function useVariant(): Variant | null {
  const location = useLocation();
  const navigate = useNavigate();

  const urlVariant = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(VARIANT_PARAM);
    return isVariant(raw) ? raw : null;
  }, [location.search]);

  // On first mount, if URL has no variant but sessionStorage has one,
  // restore it into the URL so deep-link state matches the active filter.
  useEffect(() => {
    if (urlVariant) {
      writeStoredVariant(urlVariant);
      return;
    }
    const stored = readStoredVariant();
    if (stored) {
      const params = new URLSearchParams(location.search);
      params.set(VARIANT_PARAM, stored);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlVariant]);

  return urlVariant;
}

export function filterPagesByVariant(pages: IPage[], variant: Variant | null): IPage[] {
  if (!variant) return pages;
  return pages.filter((page) => {
    // Home is always shown so users can navigate back / exit.
    if (page.href === "/") return true;
    return page.tags?.includes(variant);
  });
}

export function buildVariantHref(href: string, variant: Variant | null): string {
  if (!variant) return href;
  const [pathAndSearch, hash] = href.split("#");
  const [path, search] = pathAndSearch.split("?");
  const params = new URLSearchParams(search ?? "");
  params.set(VARIANT_PARAM, variant);
  const query = `?${params.toString()}`;
  return hash ? `${path}${query}#${hash}` : `${path}${query}`;
}

export function stripVariantFromHref(href: string): string {
  const [pathAndSearch, hash] = href.split("#");
  const [path, search] = pathAndSearch.split("?");
  const params = new URLSearchParams(search ?? "");
  params.delete(VARIANT_PARAM);
  const remaining = params.toString();
  const query = remaining ? `?${remaining}` : "";
  return hash ? `${path}${query}#${hash}` : `${path}${query}`;
}

export function clearStoredVariant() {
  writeStoredVariant(null);
}

export function pagesAvailableForVariant(variant: Variant): IPage[] {
  return allPages.filter((p) => p.href !== "/" && p.tags?.includes(variant));
}
